import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, CheckCircle2, ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { services, companyInfo, portfolioImages, openingHours } from '../data';

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Find the current service
  const service = services.find((s) => s.id === id);
  const [activeImageIndex, setActiveImageIndex] = useState(null);

  // Scroll to top on load or service change
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImageIndex(null);
  }, [id]);

  // Update SEO Meta Tags
  useEffect(() => {
    if (service) {
      document.title = `${service.title} | Peter Sloth Entreprenør`;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      const descriptionText = `${service.description} Professionelt entreprenørarbejde udføres i Horsens, Silkeborg, Skanderborg og Brædstrup. Få et uforpligtende tilbud.`;
      if (metaDescription) {
        metaDescription.setAttribute("content", descriptionText);
      }
    }
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-20 text-center">
        <h1 className="text-4xl font-extrabold text-slate-900 uppercase tracking-tight">Ydelsen blev ikke fundet</h1>
        <p className="mt-4 text-slate-600 max-w-md">Vi kunne desværre ikke finde den ydelse, du leder efter. Gå tilbage til forsiden for at se alle vores ydelser.</p>
        <Link
          to="/"
          className="mt-8 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-sm tracking-wider py-4 px-8 transition-all duration-200 rounded-none shadow-lg"
        >
          Gå til forsiden
        </Link>
      </div>
    );
  }

  // Filter portfolio images for this service
  const getRelevantCategory = (serviceId) => {
    switch (serviceId) {
      case 'sokkel':
        return 'Sokkel';
      case 'terraendaek':
        return 'Terrændæk';
      case 'belaegning':
        return 'Belægning';
      case 'stoettemur':
        return 'Støttemur';
      case 'gravearbejde':
        return 'Gravearbejde';
      case 'nedbrydning':
        return 'Nedbrydning';
      default:
        return null;
    }
  };

  const relevantCategory = getRelevantCategory(service.id);
  const relevantImages = portfolioImages.filter(
    (img) => img.category === relevantCategory
  );

  // Keyboard navigation for local lightbox
  useEffect(() => {
    if (activeImageIndex === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setActiveImageIndex(null);
      if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (prev === 0 ? relevantImages.length - 1 : prev - 1));
      }
      if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (prev === relevantImages.length - 1 ? 0 : prev + 1));
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeImageIndex, relevantImages]);
  const otherServices = services.filter((s) => s.id !== service.id);

  // Form Submission Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Honeypot check
    if (data.website_url) {
      // Quietly ignore bot submissions
      setIsSubmitting(false);
      setFormStatus({
        type: 'success',
        message: 'Tak for din henvendelse! Vi vender tilbage hurtigst muligt.',
      });
      e.target.reset();
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          service_requested: service.title,
          source_page: window.location.pathname,
        }),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din henvendelse! Vi har modtaget din besked og vender tilbage hurtigst muligt.',
        });
        e.target.reset();
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl. Prøv venligst igen eller ring til os direkte på tlf. 51 81 51 75.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen pb-16">
      {/* Hero Banner Section */}
      <section className="relative w-full text-white overflow-hidden px-4 sm:px-6 lg:px-8 z-10">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat z-0 scale-105"
          style={{ backgroundImage: `url('${service.image}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/85 to-slate-900/60 z-0" />

        <div className="relative z-10 max-w-7xl mx-auto pt-44 pb-20 sm:pt-56 sm:pb-24 md:pt-64 md:pb-28 lg:pt-72 flex flex-col items-start text-left">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 font-medium mb-6">
            <Link to="/" className="hover:text-sky-400 transition-colors">Forside</Link>
            <span>/</span>
            <Link to="/#ydelser" className="hover:text-sky-400 transition-colors">Ydelser</Link>
            <span>/</span>
            <span className="text-white font-bold">{service.title}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight uppercase leading-none max-w-4xl">
            {service.title}
          </h1>
          <p className="mt-4 text-sm sm:text-base md:text-lg text-slate-300 max-w-2xl font-medium leading-relaxed">
            {service.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content & Sidebar */}
      <section className="w-full px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
            
            {/* Left Column: Detailed Content */}
            <div className="lg:col-span-2 space-y-10 text-left">
              
              {/* Main Description */}
              <div className="w-full">
                <h2 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                  Om {service.title.toLowerCase()}
                </h2>
                <div className="space-y-6 text-slate-600 leading-relaxed text-base sm:text-lg">
                  <p className="font-bold text-slate-800 border-l-4 border-sky-500 pl-4">
                    {service.description}
                  </p>
                  <p>
                    {service.detailedText}
                  </p>
                  <p>
                    Hos Peter Sloth lægger vi stor vægt på det gode håndværk. Vi ved, at et solidt forarbejde er nøglen til et holdbart og smukt resultat, uanset om det drejer sig om støbning af fundament, belægning af en ny indkørsel eller udgravning til et nyt byggeri. Vi anvender moderne maskiner og materialer af højeste kvalitet for at sikre, at dit projekt lever op til alle standarder og forventninger.
                  </p>
                </div>

                {/* Core values list */}
                <div className="mt-10 pt-8 border-t border-slate-100">
                  <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 mb-4">
                    Fordele ved at vælge os til din opgave:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="text-black shrink-0 mt-0.5">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">Erfaren rådgivning</h4>
                        <p className="text-xs text-slate-600 mt-0.5">Vi rådgiver dig hele vejen igennem projektet, så du får den bedste løsning.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-black shrink-0 mt-0.5">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">Kvalitet til tiden</h4>
                        <p className="text-xs text-slate-600 mt-0.5">Vi overholder vores aftaler og tidsplaner, så du kan komme videre.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-black shrink-0 mt-0.5">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">Moderne maskinpark</h4>
                        <p className="text-xs text-slate-600 mt-0.5">Vi har det rette udstyr til at løse opgaven effektivt og præcist.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-black shrink-0 mt-0.5">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">Lokalt kendskab</h4>
                        <p className="text-xs text-slate-600 mt-0.5">Vi kører i hele Horsens, Silkeborg, Skanderborg og Brædstrup.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtered Portfolio Gallery */}
              {relevantImages.length > 0 && (
                <div className="bg-white p-6 sm:p-10 border border-slate-200 shadow-xs">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-100">
                    <div>
                      <h3 className="text-xl font-extrabold text-slate-900 uppercase tracking-tight">
                        Billeder af udført {service.title.toLowerCase()}
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm mt-1">
                        Her er et udvalg af vores tidligere projekter inden for {service.title.toLowerCase()}:
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {relevantImages.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className="relative group overflow-hidden border border-slate-200 shadow-xs aspect-4/3 bg-slate-100 text-left cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-sky-500"
                      >
                        <img
                          src={img.url}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        {/* Hover Overlay */}
                        <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="bg-slate-900/80 p-3 text-white">
                            <Maximize2 className="w-5 h-5" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Back to Home Button */}
              <div className="flex justify-start">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-slate-900 hover:text-sky-500 font-bold uppercase text-xs tracking-wider transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Tilbage til forsiden
                </Link>
              </div>

            </div>

            {/* Right Column: Sidebar */}
            <div className="space-y-8 lg:sticky lg:top-28">
              
              {/* Quick Quote / Contact Form */}
              <div className="bg-slate-900 text-white p-6 sm:p-8 border border-slate-800 shadow-lg">
                <h3 className="text-lg font-extrabold uppercase tracking-wider text-white mb-2">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-xs text-slate-400 mb-6">
                  Udfyld formularen, så vender vi tilbage med et uforpligtende tilbud på {service.title.toLowerCase()}.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                  {/* Honeypot field - absolutely hidden from humans */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Hjemmeside URL</label>
                    <input
                      id="website_url"
                      type="text"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Navn *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Telefon *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                        placeholder="Tlf. nr."
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">E-mail *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none"
                        placeholder="E-mail adresse"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Besked *</label>
                    <textarea
                      name="message"
                      required
                      rows={4}
                      className="w-full bg-slate-950 border border-slate-800 text-white px-3 py-2 text-sm focus:outline-hidden focus:border-sky-500 transition-colors rounded-none resize-none"
                      placeholder={`Beskriv dit projekt med ${service.title.toLowerCase()}...`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 text-white font-bold uppercase text-xs tracking-wider py-3.5 px-4 transition-colors rounded-none cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? 'Sender...' : 'Send forespørgsel'}
                    <ArrowRight className="w-4 h-4" />
                  </button>

                  {formStatus.message && (
                    <div className={`p-3 text-xs font-bold uppercase tracking-wider mt-4 ${
                      formStatus.type === 'success' ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-800' : 'bg-rose-950/50 text-rose-400 border border-rose-800'
                    }`}>
                      {formStatus.message}
                    </div>
                  )}
                </form>
              </div>

              {/* Other Services Navigation */}
              <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-100">
                  Vores andre ydelser
                </h3>
                <ul className="space-y-2">
                  {otherServices.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/ydelser/${s.id}`}
                        className="flex items-center justify-between p-2.5 text-sm font-bold text-slate-700 hover:text-sky-500 hover:bg-slate-50 transition-all border-l-2 border-transparent hover:border-sky-500"
                      >
                        <span>{s.title}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Contact Info Card */}
              <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left space-y-4">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-100">
                  Har du spørgsmål?
                </h3>
                <p className="text-xs text-slate-600">
                  Du er altid velkommen til at kontakte os for en uforpligtende snak om dit projekt.
                </p>
                <div className="space-y-3 text-xs font-bold text-slate-900">
                  <a href={`tel:${companyInfo.phoneFormatted}`} className="flex items-center gap-3 hover:text-sky-500 transition-colors">
                    <Phone className="w-4 h-4 text-sky-500 shrink-0" />
                    <span>Ring: {companyInfo.phone}</span>
                  </a>
                  <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 hover:text-sky-500 transition-colors">
                    <Mail className="w-4 h-4 text-sky-500 shrink-0" />
                    <span className="truncate">Skriv: {companyInfo.email}</span>
                  </a>
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-sky-500 shrink-0" />
                    <span>{companyInfo.address}</span>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="bg-white p-6 sm:p-8 border border-slate-200 shadow-xs text-left">
                <h3 className="text-sm font-extrabold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-sky-500" />
                  <span>Åbningstider</span>
                </h3>
                <ul className="divide-y divide-slate-200 text-xs">
                  {openingHours.map((item, index) => (
                    <li key={index} className="flex justify-between text-slate-600 font-medium py-2.5 first:pt-0 last:pb-0">
                      <span className="font-bold text-slate-800">{item.day}</span>
                      <span>{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Local Lightbox Overlay */}
      {activeImageIndex !== null && relevantImages[activeImageIndex] && (
        <div 
          className="fixed inset-0 z-50 flex flex-col justify-between bg-slate-950/95 p-4 backdrop-blur-md"
          onClick={() => setActiveImageIndex(null)}
        >
          {/* Top Bar */}
          <div className="flex justify-end items-center text-white py-2 px-4 z-10">
            <button
              onClick={() => setActiveImageIndex(null)}
              className="bg-slate-900 hover:bg-orange-500 text-white p-2.5 transition-colors rounded-none cursor-pointer"
              aria-label="Luk"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Main Image Area with Navigation */}
          <div className="relative flex-1 flex items-center justify-center max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            {/* Left Arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev === 0 ? relevantImages.length - 1 : prev - 1));
              }}
              className="absolute left-2 sm:left-4 bg-slate-900/80 hover:bg-sky-500 text-white p-3 transition-colors rounded-none z-10 cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Image */}
            <img
              src={relevantImages[activeImageIndex].url}
              alt={relevantImages[activeImageIndex].alt || 'Udført arbejde'}
              className="max-w-full max-h-full object-contain select-none shadow-2xl border border-slate-800"
            />

            {/* Right Arrow */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageIndex((prev) => (prev === relevantImages.length - 1 ? 0 : prev + 1));
              }}
              className="absolute right-2 sm:right-4 bg-slate-900/80 hover:bg-sky-500 text-white p-3 transition-colors rounded-none z-10 cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Space for layout balance */}
          <div className="h-10 z-10" />
        </div>
      )}
    </div>
  );
}
